import React, { useEffect, useState } from "react";
import { X, Download, UserPlus, Checks, Copy, Phone } from "@phosphor-icons/react";
import PageLoader from "../loader/PageLoader";
import ViewGuests from "./ManageGuestsModal/ViewGuests";
import VerificationIdSlots from "../VerificationIdSlots";
import ImageView from "../ImageView";
import useVerificationImages from "../../hooks/useVerificationImages";
import { toast } from "react-toastify";
import axiosInstance from "../../api/axiosInstance";
import { formatHotel } from "../../api/hotelTime";

function ReadOnlyField({ label, value, fallback }) {
    return (
        <div>
            <p className="text-[#8C9199] mb-2">{label}</p>
            <div className="w-full p-2 rounded-md border-[1.5px] bg-[#EEF0FB] text-[#262B33] break-words min-h-[38px]">
                {value || <span className="text-[#8C9199]">{fallback}</span>}
            </div>
        </div>
    );
}

function GuestDetails({ guest }) {
    const { urls: imgUrls, loading } = useVerificationImages(guest?.verification_id);
    const [previewUrl, setPreviewUrl] = useState("");

    const slots = [
        { url: imgUrls[0] || "", loading },
        { url: imgUrls[1] || "", loading },
    ];

    return (
        <>
            <div className="p-2 flex flex-col gap-3">
                <div className="flex items-center justify-between border-b pb-2">
                    <h3 className="font-semibold text-[#262B33]">Guest Details</h3>
                </div>

                {guest.is_minor && (
                    <div>
                        <div className="flex items-center justify-between p-3 bg-[#FFF8F0] border border-[#FED7AA] rounded-lg">
                            <span className="flex items-center gap-2 font-medium text-[#9A3412]">
                                🧒 This guest is a kid (minor)
                            </span>
                            <span className="relative inline-flex">
                                <span className="w-10 h-6 bg-[#FF7378] rounded-full relative after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:w-5 after:h-5 after:bg-white after:rounded-full after:shadow after:translate-x-4"></span>
                            </span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1.5">
                            Only the kid's name is needed — contact, email, address and ID are not
                            required.
                        </p>
                    </div>
                )}

                {!guest.is_minor && (
                    <div>
                        <h4>Contact</h4>
                        <div className="flex items-center gap-2 p-2 border border-[#BFC4CC] bg-[#EEF0FB] rounded-md">
                            <Phone size={19} className="shrink-0" />
                            {guest.contact ? (
                                `+${guest.contact}`
                            ) : (
                                <span className="text-[#8C9199]">Not provided</span>
                            )}
                        </div>
                    </div>
                )}

                <div>
                    <h4>Name</h4>
                    <div className="w-full p-2 border border-[#BFC4CC] bg-[#EEF0FB] rounded-lg">
                        {guest.name || <span className="text-[#8C9199]">Not provided</span>}
                    </div>
                </div>

                {!guest.is_minor && (
                    <>
                        <div>
                            <h4>Email</h4>
                            <div className="w-full p-2 border border-[#BFC4CC] bg-[#EEF0FB] rounded-lg">
                                {guest.email || (
                                    <span className="text-[#8C9199]">Not provided</span>
                                )}
                            </div>
                        </div>

                        <div>
                            <h4>Address</h4>
                            <div className="w-full p-2 border border-[#BFC4CC] bg-[#EEF0FB] rounded-lg">
                                {guest.address || (
                                    <span className="text-[#8C9199]">Not provided</span>
                                )}
                            </div>
                        </div>

                        <div>
                            <h4>Verification ID</h4>
                            <VerificationIdSlots
                                slots={slots}
                                readOnly={true}
                                disabled={true}
                                onPreview={setPreviewUrl}
                                onClear={() => {}}
                                onSelectFile={() => {}}
                            />
                        </div>

                        <div className="flex items-center">
                            <input
                                type="checkbox"
                                className="mr-2"
                                id="manualVerificationPastGuest"
                                checked={Boolean(guest.manual_verification)}
                                disabled={true}
                                readOnly
                            />
                            <label
                                htmlFor="manualVerificationPastGuest"
                                className="text-md text-[#262B33]"
                            >
                                ID Manually Verified By Receptionist
                            </label>
                        </div>
                    </>
                )}
            </div>

            {previewUrl && (
                <ImageView
                    imgModal={{ imgUrl: previewUrl, openModal: true }}
                    setImgModal={() => setPreviewUrl("")}
                />
            )}
        </>
    );
}

export default function PastBookingCard(props) {
    const [data, setData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [codeCopied, setCodeCopied] = useState(false);

    const [activeRoom, setActiveRoom] = useState("");
    const [activeGuest, setActiveGuest] = useState(0);

    useEffect(() => {
        const fetchGuests = async () => {
            try {
                setIsLoading(true);
                const response = await axiosInstance.get(
                    `/api/v1/hotels/${props.hotelId}/bookings/${props.bookingId}/booked-rooms`,
                );
                const rooms = response.data.data;
                if (Array.isArray(rooms) && rooms.length > 0) {
                    const sortedData = [...rooms].sort(
                        (a, b) => a.room_number - b.room_number,
                    );
                    setData(sortedData);
                    setActiveRoom(sortedData[0].id);
                    setActiveGuest(0);
                }
            } catch (error) {
                console.log(error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchGuests();
    }, []);

    const closeCard = () => {
        props.setPastBookingCard(false);
    };

    const handleRoomClick = (id) => {
        setActiveRoom(id);
        setActiveGuest(0);
    };

    const bookingCode = props.booking?.booking_code || "";

    const copyBookingCode = () => {
        navigator.clipboard.writeText(bookingCode);
        setCodeCopied(true);
        setTimeout(() => setCodeCopied(false), 1500);
    };

    const handleDownload = async () => {
        try {
            setDownloading(true);
            const response = await axiosInstance.get(
                `/api/v1/hotels/${props.hotelId}/bookings/${props.bookingId}/download-pdf`,
                { responseType: "blob" },
            );
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute(
                "download",
                `booking-details-${props.booking?.ota_id || props.bookingId || ""}.pdf`,
            );
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            toast.error("Failed to download pdf please re-download");
            console.error("Download failed:", error);
        } finally {
            setDownloading(false);
        }
    };

    const activeRoomData = data.find((room) => room.id === activeRoom);
    const guests = activeRoomData?.guests || [];
    const guestActive = guests[activeGuest];

    const stayFormat = "DD/MM/YYYY, hh:mm A";

    return (
        <>
            {(isLoading || downloading) && <PageLoader />}
            <div
                className="fixed top-0 left-0 h-screen w-screen bg-black bg-opacity-60 flex items-center justify-center z-[1001] overflow-auto"
                onMouseDown={(e) => {
                    if (e.target === e.currentTarget) closeCard();
                }}
            >
                <div className="pastBookingCard popup-content bg-white p-6 rounded-xl shadow-2xl text-sm flex flex-col gap-3 w-[80%] md:w-[70%] lg:w-[55%] max-h-[98vh] overflow-y-auto">
                    {/* Header */}
                    <div className="flex items-center justify-between border-b pb-3">
                        <div>
                            <h2 className="text-xl font-semibold">Past Booking</h2>
                            <p className="text-xs text-gray-400 mt-0.5">
                                View-only record of a completed stay
                            </p>
                        </div>
                        <button
                            onClick={closeCard}
                            className="hover:bg-[#F2F7FF] p-1.5 border-[1.5px] rounded-md"
                            type="button"
                        >
                            <X weight="bold" size={18} />
                        </button>
                    </div>

                    {/* Booking code and stay dates */}
                    <div className="grid grid-cols-3 gap-4">
                        <div>
                            <p className="text-[#8C9199] mb-2">Booking Code</p>
                            <div className="flex items-center gap-2 py-1">
                                <span
                                    className="font-mono font-semibold tracking-wider text-[#262B33] bg-[#EEF0FB] border-[1.5px] border-[#BFC4CC] rounded-md px-2.5 py-1 truncate"
                                    title={bookingCode || "NA"}
                                >
                                    {bookingCode || "NA"}
                                </span>
                                {bookingCode && (
                                    <button
                                        type="button"
                                        className="text-[#8C9199] hover:text-[#0B22B2] shrink-0"
                                        title="Copy booking code"
                                        onClick={copyBookingCode}
                                    >
                                        {codeCopied ? <Checks size={16} /> : <Copy size={16} />}
                                    </button>
                                )}
                            </div>
                        </div>
                        <ReadOnlyField
                            label="Check-in Date"
                            value={formatHotel(data[0]?.checkin_date, stayFormat)}
                            fallback="NA"
                        />
                        <ReadOnlyField
                            label="Check-out Date"
                            value={formatHotel(data[0]?.checkout_date, stayFormat)}
                            fallback="NA"
                        />
                    </div>

                    {/* Booker details */}
                    <div className="grid grid-cols-3 gap-4">
                        <ReadOnlyField
                            label="Booking Name"
                            value={props.booking?.booking_name}
                            fallback="Not provided"
                        />
                        <ReadOnlyField
                            label="OTA ID"
                            value={props.booking?.ota_id}
                            fallback="Not provided"
                        />
                        <div>
                            <p className="text-[#8C9199] mb-2">Contact</p>
                            <div className="border-[1.5px] rounded-md flex w-full overflow-hidden bg-[#EEF0FB] min-h-[38px]">
                                <div className="rounded-md w-full flex items-center gap-2 px-2 min-w-0">
                                    <Phone size={19} className="shrink-0" />
                                    {props.booking?.booking_contact ? (
                                        `+${props.booking.booking_contact}`
                                    ) : (
                                        <span className="text-[#8C9199]">Not provided</span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Room Buttons */}
                    <div className="my-2 virtualRooms flex gap-2 max-w-full min-h-10 overflow-x-auto">
                        {data.map((room, index) => (
                            <button
                                key={room.id}
                                className={`px-4 py-2 rounded-full flex-shrink-0 flex items-center gap-2 font-medium transition-colors
                                ${
                                    room.id === activeRoom
                                        ? "bg-[#ff7378] text-white shadow-md"
                                        : "bg-gray-100 hover:bg-gray-200 text-gray-600"
                                }`}
                                onClick={() => handleRoomClick(room.id)}
                                type="button"
                            >
                                <span>Room {index + 1}</span>
                            </button>
                        ))}
                    </div>

                    {/* Room Number */}
                    {activeRoomData && (
                        <div className="my-2">
                            <h4>Room Number</h4>
                            <div className="w-full my-1 p-2 border border-[#BFC4CC] bg-[#EEF0FB] rounded-md">
                                {activeRoomData.room_number || (
                                    <span className="text-[#8C9199]">Not assigned</span>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Guests */}
                    <div className="flex gap-2 border-t border-[#BFC4CC]">
                        {/* Guest cards */}
                        <div className="flex-[0.4] p-2 border-r border-[#BFC4CC] overflow-hidden">
                            <div className="viewGuests flex flex-col gap-2 h-[520px] overflow-y-auto relative p-2">
                                {guests.length > 0 ? (
                                    guests.map((user, index) => (
                                        <ViewGuests
                                            key={index}
                                            user={user}
                                            guestIndex={index}
                                            activeUser={activeGuest}
                                            onSelect={setActiveGuest}
                                            readOnly={true}
                                        />
                                    ))
                                ) : (
                                    <div className="h-full flex flex-col items-center justify-center gap-2 text-center px-4 text-[#8C9199]">
                                        <UserPlus size={32} />
                                        <p className="font-medium text-[#262B33]">
                                            No guests in this room
                                        </p>
                                        <p className="text-xs">
                                            No guest details were captured for this room during
                                            the stay.
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Guest details */}
                        <div className="flex-[0.6]">
                            {guestActive ? (
                                <GuestDetails
                                    guest={guestActive}
                                    key={`${activeRoom}-${activeGuest}`}
                                />
                            ) : (
                                <div className="h-[520px] flex flex-col items-center justify-center gap-2 text-center px-6 text-[#8C9199]">
                                    <UserPlus size={32} />
                                    <p className="font-medium text-[#262B33]">
                                        No guest selected
                                    </p>
                                    <p className="text-xs">
                                        Pick a guest on the left to see their details.
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Download */}
                    <div className="flex flex-col gap-4 mt-2 text-center">
                        <button
                            type="button"
                            className={`w-full p-2 rounded-lg text-white text-center flex justify-center items-center gap-4 ${
                                downloading ? "bg-[#8C9199] cursor-not-allowed" : "bg-[#0B22B2]"
                            }`}
                            disabled={downloading}
                            onClick={handleDownload}
                        >
                            {downloading ? "Preparing…" : "Download PDF"}
                            <Download size={20} />
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
}
